import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import {
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

const AuthContext = createContext(null);

// Custom error class for admin-specific errors
class AdminAuthError extends Error {
  constructor(message) {
    super(message);
    this.name = 'AdminAuthError';
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Function to check if user is admin
  const checkAdminStatus = async (user) => {
    try {
      console.log('=== ADMIN STATUS CHECK START ===');
      console.log('User object:', user);
      console.log('User email:', user.email);
      console.log('User UID:', user.uid);

      // Test Firestore connection first
      console.log('Testing Firestore connection...');
      console.log('DB object:', db);

      const userRef = doc(db, 'users', user.uid);
      console.log('User document reference:', userRef);
      console.log('User document path:', userRef.path);

      console.log('Attempting to fetch user document...');

      // Add timeout to the getDoc call
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('getDoc timeout after 10 seconds')), 10000);
      });

      const userDoc = await Promise.race([
        getDoc(userRef),
        timeoutPromise
      ]);

      console.log('getDoc call completed successfully');
      console.log('User document exists:', userDoc.exists());

      if (!userDoc.exists()) {
        console.log('❌ No user document found for UID:', user.uid);
        return false;
      }

      const userData = userDoc.data();
      console.log('✅ User document data:', userData);
      console.log('Admin field from document:', userData.admin);
      console.log('Email from document:', userData.email);
      console.log('User email from auth:', user.email);

      // Check if admin field is true and email matches
      const isAdmin = userData.admin === true;
      const emailMatch = userData.email === user.email;
      const isUserAdmin = isAdmin && emailMatch;

      console.log('Admin field is true:', isAdmin);
      console.log('Email match:', emailMatch);
      console.log('Final admin status:', isUserAdmin);
      console.log('=== ADMIN STATUS CHECK END ===');

      return isUserAdmin;
    } catch (error) {
      console.error('❌ Error checking admin status:', error);
      console.error('Error name:', error.name);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('Full error object:', error);
      console.log('=== ADMIN STATUS CHECK END (ERROR) ===');
      return false;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          console.log('Auth state changed - User:', user.email);
          const isUserAdmin = await checkAdminStatus(user);

          if (!isUserAdmin) {
            console.log('User is not admin, signing out');
            await signOut(auth);
            setUser(null);
            setIsAdmin(false);
            navigate('/login');
          } else {
            console.log('User is admin, setting user state');
            setUser(user);
            setIsAdmin(true);
            // Don't navigate here - let the App component handle routing
          }
        } catch (error) {
          console.error('Error in auth state change:', error);
          await signOut(auth);
          setUser(null);
          setIsAdmin(false);
          navigate('/login');
        }
      } else {
        console.log('Auth state changed - No user');
        setUser(null);
        setIsAdmin(false);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);



  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });

      console.log('Starting Google sign in...');
      const userCredential = await signInWithPopup(auth, provider);
      console.log('Google sign in successful:', userCredential.user.email);

      // Check if user is an admin
      const isUserAdmin = await checkAdminStatus(userCredential.user);

      if (!isUserAdmin) {
        console.log('User is not admin, signing out');
        await signOut(auth);
        throw new AdminAuthError('Unauthorized access. Admin privileges required.');
      }

      return userCredential.user;
    } catch (error) {
      console.error('Google sign in error:', error);
      if (error instanceof AdminAuthError) {
        throw error;
      }
      // Handle Firebase auth errors
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          throw new Error('Sign in was cancelled');
        case 'auth/popup-blocked':
          throw new Error('Pop-up was blocked by the browser');
        case 'auth/cancelled-popup-request':
          throw new Error('Sign in was cancelled');
        default:
          throw new Error('An error occurred during Google sign in');
      }
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      navigate('/login');
    }
  };

  const value = {
    user,
    isAdmin,
    isAuthenticated: !!user && isAdmin,
    isLoading,
    loginWithGoogle,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return isAuthenticated ? children : null;
}; 